import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Trash2 } from 'lucide-react';
import SearchSelect from '../components/shared/SearchSelect';
import RefLink from '../components/shared/RefLink';
import { Field, FilePicker, inputCls, btnGhost } from '../components/shared/FormKit';
import { FormValidationProvider, useFormValidation, FieldInvalidInline } from '../components/shared/formValidation';
import { FieldCommentInline } from '../components/workflow/fieldComments';
import { FormShell, FormSection } from '../components/workflow/FormShell';
import ReviewerSelect, { MASTER_REVIEWER_HINT, reviewerPickOf } from '../components/workflow/ReviewerSelect';
import { EntityCategorySelect, uenHintFor } from '../components/shared/entityCategory';
import { fetchJSON } from '../utils/api';
import { hintOf } from '../utils/uploadKinds';

// New Client registration — submission form (docs/modules/workflow-client.md).
// One request = one client group + one or more NEW companies (same group).
// The group is either the company itself (standalone — one company only) or
// a real group (existing / new, with an optional structure diagram). Every
// company needs an Appointment Letter: the request's shared one (default),
// its own upload, or one of the group's existing letters. Approval writes the
// group and every company into the Clients & Entities master; Engagement
// Registration then selects the registered entities.
//
// This page is the REFERENCE shape: the field blocks, the required-field
// ruler and the FormData builder are exported, and the Detail page's Modify
// modal renders / validates / posts the very same things. What stays in each
// surface is what only that surface has (supporting files and remarks layout
// on this page; the removable current attachments in Modify).

export const APPOINTMENT_DOC_TYPE = 'APPOINTMENT_LETTER';
export const STRUCTURE_DOC_TYPE = 'GROUP_STRUCTURE';
// The request's SHARED letter (not bound to a company row).
export const appointmentFileOf = (files) =>
  (files || []).find((x) => x.docType === APPOINTMENT_DOC_TYPE && !x.entityRowId) || null;
// One company row's OWN letter.
export const ownLetterFileOf = (files, rowId) =>
  (files || []).find((x) => x.docType === APPOINTMENT_DOC_TYPE && x.entityRowId === rowId) || null;
export const structureFileOf = (files) =>
  (files || []).find((x) => x.docType === STRUCTURE_DOC_TYPE) || null;

// The marking / comment key of one company row (and of its fields).
export const rowFieldKey = (key, field = null) => (field ? `entity.${key}.${field}` : `entity.${key}`);

let rowSeq = 0;
export const emptyCompanyRow = () => ({
  key: `n${++rowSeq}`,       // client-side key — an own letter upload is named after it
  id: null,                  // the stored row id (Modify); null on a new row
  entityName: '',
  uen: '',
  entityCategory: 'local',   // 'local' | 'government' | 'overseas' — most clients are Singapore companies
  letterMode: 'shared',      // 'shared' | 'own' | 'existing'
  appointmentLetterId: '',   // 'existing' only
  dateOfAppointment: '',     // own / existing rows type their own; shared rows take the request's
});

// The default empty form state (Modify prefills from the request instead).
export const emptyClientForm = () => ({
  groupType: 'standalone',   // 'standalone' | 'group'
  groupMode: 'existing',     // 'existing' | 'new'
  groupPick: '',
  newGroupName: '',
  // The SHARED Appointment Letter: upload one, or — for an existing group
  // that already holds one — point at that group letter.
  appointmentMode: 'upload', // 'upload' | 'existing'
  appointmentLetterId: '',
  // The Date of Appointment that goes with the shared letter — one date for
  // every company using it (each company is written with its own copy).
  sharedDateOfAppointment: '',
  remarks: '',
  // The reviewer the submitter names (one Partner, or the QM Team).
  reviewerStaffId: '',
  entities: [emptyCompanyRow()],
});

// Modify prefill. The group shape is inferred from the stored request:
// standalone when the group name equals the (single) company name; otherwise
// a group (existing when a group id is set). Row keys = the stored row ids.
export const clientFormFromRequest = (request) => {
  const rows = request.entities || [];
  const standalone = rows.length === 1 && (request.clientGroup || '').trim().toLowerCase()
    === (rows[0].entityName || '').trim().toLowerCase();
  return {
    groupType: standalone ? 'standalone' : 'group',
    groupMode: request.clientGroupId ? 'existing' : 'new',
    groupPick: request.clientGroupId ? String(request.clientGroupId) : '',
    newGroupName: request.clientGroupId || standalone ? '' : (request.clientGroup || ''),
    appointmentMode: request.appointmentLetterId ? 'existing' : 'upload',
    appointmentLetterId: request.appointmentLetterId ? String(request.appointmentLetterId) : '',
    sharedDateOfAppointment: (rows.find((e) => (e.letterMode || 'shared') === 'shared') || {}).dateOfAppointment || '',
    remarks: request.remarks || '',
    reviewerStaffId: reviewerPickOf(request),
    entities: rows.map((e) => ({
      key: String(e.id),
      id: e.id,
      entityName: e.entityName || '',
      uen: e.uen || '',
      entityCategory: e.entityCategory || '',
      letterMode: e.letterMode || 'shared',
      appointmentLetterId: e.appointmentLetterId ? String(e.appointmentLetterId) : '',
      dateOfAppointment: e.dateOfAppointment || '',
    })),
  };
};

// Client-side required check → `[{ key, label }]` (server re-validates). `key`
// is the `field=` key of the empty control, so `formValidation` can mark it on
// the page; `label` is the banner wording. Every company needs a Date of
// Appointment (the shared one covers every 'shared' row); the shared letter
// itself is not required. A company row set to 'own' needs a letter that
// exists after this save: a new upload or — Modify only — an already-uploaded
// one that survives the save (`keptOwnRowIds`).
export const clientRequiredGaps = (f, { rowLetters = {}, keptOwnRowIds = [] } = {}) => {
  const gaps = [];
  const isGroup = f.groupType === 'group';
  if (isGroup && f.groupMode === 'existing' && !f.groupPick) gaps.push({ key: 'groupPick', label: 'Existing Group' });
  if (isGroup && f.groupMode === 'new' && !f.newGroupName.trim()) gaps.push({ key: 'newGroupName', label: 'New Group Name' });
  (f.entities || []).forEach((row, i) => {
    const n = i + 1;
    if (!row.entityName.trim()) gaps.push({ key: rowFieldKey(row.key, 'entityName'), label: `Company ${n} Entity Name` });
    if (!row.entityCategory) gaps.push({ key: rowFieldKey(row.key, 'entityCategory'), label: `Company ${n} Entity Category` });
    if (!row.uen.trim()) gaps.push({ key: rowFieldKey(row.key, 'uen'), label: `Company ${n} UEN` });
    if (row.letterMode === 'own' && !rowLetters[row.key] && !(row.id && keptOwnRowIds.includes(row.id))) {
      gaps.push({ key: rowFieldKey(row.key, 'letter'), label: `Company ${n} Appointment Letter` });
    }
    if (row.letterMode === 'existing' && !row.appointmentLetterId) {
      gaps.push({ key: rowFieldKey(row.key, 'appointmentLetterId'), label: `Company ${n} Group Letter` });
    }
    if (row.letterMode !== 'shared' && !row.dateOfAppointment) {
      gaps.push({ key: rowFieldKey(row.key, 'dateOfAppointment'), label: `Company ${n} Date of Appointment` });
    }
  });
  if ((f.entities || []).some((r) => (r.letterMode || 'shared') === 'shared') && !f.sharedDateOfAppointment) {
    gaps.push({ key: 'sharedDateOfAppointment', label: 'Date of Appointment' });
  }
  if (!f.reviewerStaffId) gaps.push({ key: 'reviewerName', label: 'Reviewer' });
  return gaps;
};

/** The same check as a list of labels. */
export const missingClientFields = (f, opts) => clientRequiredGaps(f, opts).map((g) => g.label);

// FormData for POST/PATCH — one shape for both. `entities` travels as JSON
// (with each stored row's id so Modify updates in place); an own letter is
// posted as `letter_<rowKey>`. appointmentLetterId only travels in 'existing'
// mode: on PATCH an omitted id clears the stored reference, which is exactly
// what switching back to an upload means.
export const clientFormData = (f, { structureFile = null, appointmentFile = null, rowLetters = {}, files = [] } = {}) => {
  const form = new FormData();
  form.set('remarks', f.remarks);
  if (f.reviewerStaffId) form.set('reviewerStaffId', f.reviewerStaffId);
  const first = (f.entities || [])[0];
  if (f.groupType !== 'group') {
    // Standalone: the client group is the company itself.
    form.set('clientGroup', (first ? first.entityName : '').trim());
  } else if (f.groupMode === 'existing') {
    if (f.groupPick) form.set('clientGroupId', f.groupPick);
    if (structureFile) form.append('filesGroupStructure', structureFile);
  } else {
    form.set('clientGroup', f.newGroupName.trim());
    if (structureFile) form.append('filesGroupStructure', structureFile);
  }
  form.set('entities', JSON.stringify((f.entities || []).map((row) => ({
    key: row.key,
    id: row.id || null,
    entityName: row.entityName.trim(),
    uen: row.uen.trim(),
    entityCategory: row.entityCategory,
    letterMode: row.letterMode,
    appointmentLetterId: row.letterMode === 'existing' && row.appointmentLetterId ? String(row.appointmentLetterId) : null,
    // A shared-letter company carries the request's date; the others their own.
    dateOfAppointment: (row.letterMode === 'shared' ? f.sharedDateOfAppointment : row.dateOfAppointment) || null,
  }))));
  (f.entities || []).forEach((row) => {
    if (row.letterMode === 'own' && rowLetters[row.key]) form.append(`letter_${row.key}`, rowLetters[row.key]);
  });
  if (f.appointmentMode === 'existing') {
    if (f.appointmentLetterId) form.set('appointmentLetterId', String(f.appointmentLetterId));
  } else if (appointmentFile) {
    form.append('filesAppointment', appointmentFile);
  }
  files.forEach((x) => form.append('files', x));
  return form;
};

// ── Live "already taken" check ─────────────────────────────────────────────
//
// As soon as a row's name / UEN is typed the form asks the server whether the
// company is already in the master or on another pending request, and says
// so under that row — not only on submit. Rows that repeat a name / UEN
// within the form are flagged here too. The server is still the authority:
// POST / PATCH re-run the same lookup. A failed lookup reports nothing rather
// than guessing.
//
// Returns { [rowKey]: [{ field, code, message, requestId, requestNo }] }.
export const useCompanyChecks = (entities, requestId = null) => {
  const [remote, setRemote] = useState({});
  const key = JSON.stringify((entities || []).map((r) => [r.key, r.entityName.trim(), r.uen.trim().toUpperCase()]));
  const reqRef = useRef(0);
  useEffect(() => {
    const rows = JSON.parse(key);
    const mine = ++reqRef.current;
    const timer = setTimeout(async () => {
      const out = {};
      await Promise.all(rows.map(async ([rowKey, name, uen]) => {
        if (!name && !uen) return;
        try {
          const qs = new URLSearchParams();
          if (uen) qs.set('uen', uen);
          if (name) qs.set('entityName', name);
          if (requestId) qs.set('requestId', String(requestId));
          const data = await fetchJSON(`/api/workflow/client/check?${qs.toString()}`);
          const hits = [];
          if (data.uen) hits.push({ field: 'uen', ...data.uen });
          if (data.entityName) hits.push({ field: 'entityName', ...data.entityName });
          if (hits.length) out[rowKey] = hits;
        } catch (e) { /* advisory — the write path still refuses */ }
      }));
      if (reqRef.current === mine) setRemote(out);
    }, 400);
    return () => clearTimeout(timer);
  }, [key, requestId]);

  return useMemo(() => {
    const rows = JSON.parse(key);
    const out = {};
    rows.forEach(([rowKey, name, uen], i) => {
      const hits = [...(remote[rowKey] || [])];
      for (let j = 0; j < i; j += 1) {
        const [, otherName, otherUen] = rows[j];
        if (uen && uen === otherUen) hits.push({ field: 'uen', code: 'DUPLICATE_IN_REQUEST', message: `UEN ${uen} is already entered for Company ${j + 1} on this request.` });
        if (name && name.toLowerCase() === otherName.toLowerCase()) hits.push({ field: 'entityName', code: 'DUPLICATE_IN_REQUEST', message: `"${name}" is already entered as Company ${j + 1} on this request.` });
      }
      if (hits.length) out[rowKey] = hits;
    });
    return out;
  }, [key, remote]);
};

/** Any row blocked by the live check? (disables Submit / Save) */
export const hasCompanyBlockers = (checks) => Object.keys(checks || {}).length > 0;

const CompanyBlockers = ({ hits }) => {
  if (!hits || hits.length === 0) return null;
  return (
    <div className="mt-2 border border-amber-200 rounded-lg p-3 bg-amber-50 text-sm text-amber-800 space-y-1">
      <p className="font-medium">This company cannot be registered as entered.</p>
      {hits.map((h, i) => (
        <p key={`${h.field}-${i}`}>
          {h.message}
          {h.requestId && (
            <>
              {' '}
              <RefLink to={`/workflow/client/${h.requestId}`} className="font-medium underline hover:text-amber-900">Open {h.requestNo}</RefLink>
            </>
          )}
        </p>
      ))}
    </div>
  );
};

// ── Shared form body ───────────────────────────────────────────────────────
//
// ① client group → ② companies (one card each, with its letter choice) →
// ③ the shared Appointment Letter. Both surfaces render this block, so the
// group-letter lookup lives HERE — neither surface can ship without the
// "covered by an existing group letter" path. `existingFiles` (Modify only,
// minus the ones marked for removal) switches the hints to "what is already
// attached" wording. The `field=` anchors use the Detail page's keys (row
// keys for companies); outside a workflow detail page they render nothing.
export const ClientFormFields = ({
  f, set, groups = [], existingFiles = [],
  structureFile, setStructureFile,
  appointmentFile, setAppointmentFile,
  rowLetters = {}, setRowLetter,
  checks = {},
}) => {
  const [groupLetters, setGroupLetters] = useState([]);
  const isGroup = f.groupType === 'group';
  const currentLetter = appointmentFileOf(existingFiles);
  const currentStructure = structureFileOf(existingFiles);
  const rows = f.entities || [];
  const anyShared = rows.some((r) => r.letterMode === 'shared');

  const setRows = (next) => set('entities', next);
  const setRow = (key, patch) => setRows(rows.map((r) => (r.key === key ? { ...r, ...patch } : r)));
  const addRow = () => {
    setRows([...rows, emptyCompanyRow()]);
    // Two companies are a group by definition.
    if (!isGroup) set('groupType', 'group');
  };
  const removeRow = (key) => {
    if (rows.length <= 1) return;
    setRows(rows.filter((r) => r.key !== key));
    if (setRowLetter) setRowLetter(key, null);
  };

  // The picked EXISTING group's current letters feed the "covered by an
  // existing letter" options (shared and per company); a new group /
  // standalone client must upload.
  useEffect(() => {
    if (!(isGroup && f.groupMode === 'existing' && f.groupPick)) {
      setGroupLetters([]);
      if (f.appointmentMode !== 'upload') set('appointmentMode', 'upload');
      if (f.appointmentLetterId) set('appointmentLetterId', '');
      if (rows.some((r) => r.letterMode === 'existing' || r.appointmentLetterId)) {
        setRows(rows.map((r) => (r.letterMode === 'existing' ? { ...r, letterMode: 'shared', appointmentLetterId: '' } : r)));
      }
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const data = await fetchJSON(`/api/workflow/client/group-letters?groupId=${f.groupPick}`);
        if (cancelled) return;
        const letters = data.letters || [];
        setGroupLetters(letters);
        const known = (id) => letters.some((l) => String(l.id) === String(id));
        // A stored pick that does not belong to the (re)picked group cannot
        // be submitted — fall back to uploading.
        const pickGone = f.appointmentLetterId && !known(f.appointmentLetterId);
        if (pickGone) set('appointmentLetterId', '');
        if (f.appointmentMode === 'existing' && (letters.length === 0 || pickGone)) {
          set('appointmentMode', 'upload');
        }
        const fixed = rows.map((r) => {
          if (r.letterMode !== 'existing') return r;
          if (letters.length === 0) return { ...r, letterMode: 'shared', appointmentLetterId: '' };
          return known(r.appointmentLetterId) ? r : { ...r, appointmentLetterId: '' };
        });
        if (fixed.some((r, i) => r !== rows[i])) setRows(fixed);
      } catch (e) { /* letters are a convenience — upload still works */ }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isGroup, f.groupMode, f.groupPick]);

  const letterOptions = (value, onChange) => (
    <select value={value} onChange={(e) => onChange(e.target.value)} className={inputCls}>
      <option value="">Select…</option>
      {groupLetters.map((l) => (
        <option key={l.id} value={l.id}>{l.originalName}{l.sourceRequestNo ? ` (${l.sourceRequestNo})` : ''}</option>
      ))}
    </select>
  );

  return (
    <>
      <FormSection step={1} title="Client Group">
        <Field label="Client Group" required field="clientGroup">
          <div className="flex flex-wrap gap-4">
            {[['standalone', 'Not a group'], ['group', 'Part of a group']].map(([v, label]) => (
              <label key={v} className={`flex items-center gap-2 text-sm ${v === 'standalone' && rows.length > 1 ? 'text-slate-400' : 'text-slate-700'}`}>
                <input type="radio" name="groupType" value={v} checked={f.groupType === v}
                  disabled={v === 'standalone' && rows.length > 1}
                  onChange={() => set('groupType', v)} />
                {label}
              </label>
            ))}
          </div>
          <div className="mt-2 border-l-2 border-slate-200 pl-3 text-xs text-slate-500 leading-relaxed">
            <p>Select <span className="font-medium text-slate-700">Part of a group</span> when, for example:</p>
            <ol className="list-[lower-roman] pl-5 mt-0.5 space-y-0.5">
              <li>The audit entity (only one entity) is part of an overseas group, i.e. the holding company is overseas.</li>
              <li>There is more than one audit entity in Singapore, all of them sister entities, with no consolidation.</li>
            </ol>
          </div>
          {isGroup && (
            <div className="space-y-3 border border-slate-200 rounded-lg p-3 bg-slate-50 mt-2">
              {rows.length > 1 && (
                <p className="text-xs text-slate-500">Several companies on one request are always a group — every company below is registered in this group.</p>
              )}
              <div className="flex flex-wrap gap-4">
                {[['existing', 'Existing group'], ['new', 'New group']].map(([v, label]) => (
                  <label key={v} className="flex items-center gap-2 text-xs text-slate-600">
                    <input type="radio" name="groupMode" value={v} checked={f.groupMode === v}
                      onChange={() => { set('groupMode', v); set('groupPick', ''); set('newGroupName', ''); }} />
                    {label}
                  </label>
                ))}
              </div>
              {/* Neither control is a Field of its own (they sit inside the
                  Client Group field), so each carries its own "Required" mark. */}
              {f.groupMode === 'existing' ? (
                <div>
                  <SearchSelect
                    value={f.groupPick}
                    onChange={(v) => set('groupPick', v)}
                    options={groups.map((g) => ({ value: g.id, label: g.name }))}
                    placeholder="Search group…"
                  />
                  <FieldInvalidInline field="groupPick" text="Required — pick the existing group" />
                </div>
              ) : (
                <div>
                  <input type="text" value={f.newGroupName} onChange={(e) => set('newGroupName', e.target.value)} className={inputCls} placeholder="New group name" />
                  <FieldInvalidInline field="newGroupName" text="Required — name the new group" />
                </div>
              )}
              <Field
                label="Group Structure Diagram"
                hint={currentStructure
                  ? `Currently attached: ${currentStructure.originalName}. A new file is added as another version — remove the current one in the attachments list if it should not stay on the request.`
                  : "Approval adds it to the group's structure history. PDF / DOC / DOCX / JPG / PNG."}
              >
                <FilePicker
                  kind="docsAndImages"
                  onChange={(e) => setStructureFile((e.target.files || [])[0] || null)}
                  files={structureFile}
                />
              </Field>
            </div>
          )}
        </Field>
      </FormSection>

      <FormSection step={2} title="Companies" hint="One row per company to register — all in the client group above. Each company says where its Appointment Letter comes from.">
        <div className="space-y-4">
          {rows.map((row, i) => {
            const currentOwn = row.id ? ownLetterFileOf(existingFiles, row.id) : null;
            const picked = rowLetters[row.key];
            return (
              <div key={row.key} className="border border-slate-200 rounded-lg p-4 space-y-4">
                <div className="flex items-center justify-between gap-2">
                  <p className="text-sm font-semibold text-slate-800">Company {i + 1}</p>
                  {rows.length > 1 && (
                    <button type="button" onClick={() => removeRow(row.key)} className={`${btnGhost} inline-flex items-center gap-1 text-red-600 hover:bg-red-50`}>
                      <Trash2 className="w-4 h-4" /> Remove
                    </button>
                  )}
                </div>
                <Field label="Entity Name" required field={rowFieldKey(row.key, 'entityName')}>
                  <input type="text" value={row.entityName} onChange={(e) => setRow(row.key, { entityName: e.target.value })} className={inputCls} />
                </Field>
                {/* Category first: the UEN guidance under the next field follows it. */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 items-start">
                  <Field label="Entity Category" required field={rowFieldKey(row.key, 'entityCategory')} hint="Where the company is registered — not whether the engagement's client is a PIE.">
                    <EntityCategorySelect value={row.entityCategory} onChange={(v) => setRow(row.key, { entityCategory: v })} className={inputCls} />
                  </Field>
                  <Field label="UEN" required field={rowFieldKey(row.key, 'uen')} hint={uenHintFor(row.entityCategory)}>
                    <input type="text" value={row.uen} onChange={(e) => setRow(row.key, { uen: e.target.value })} className={inputCls} />
                  </Field>
                </div>
                <CompanyBlockers hits={checks[row.key]} />

                <Field label="Appointment Letter" required field={rowFieldKey(row.key, 'letterMode')}>
                  <div className="flex flex-wrap gap-4">
                    {[
                      ['shared', "This request's letter"],
                      ['own', 'Its own letter'],
                      ...(groupLetters.length > 0 ? [['existing', 'An existing group letter']] : []),
                    ].map(([v, label]) => (
                      <label key={v} className="flex items-center gap-2 text-sm text-slate-700">
                        <input type="radio" name={`letterMode_${row.key}`} value={v} checked={row.letterMode === v}
                          onChange={() => setRow(row.key, { letterMode: v, appointmentLetterId: '' })} />
                        {label}
                      </label>
                    ))}
                  </div>
                  {row.letterMode === 'shared' ? (
                    <p className="text-xs text-slate-500 mt-2">Covered by the Appointment Letter and Date of Appointment in the next section (one letter, one date, for several companies of the same group).</p>
                  ) : (
                    <div className="mt-3">
                      <Field label="Date of Appointment" required field={rowFieldKey(row.key, 'dateOfAppointment')} hint="The date this company appointed the firm — recorded on the client master; engagements read it from there.">
                        <input type="date" value={row.dateOfAppointment} onChange={(e) => setRow(row.key, { dateOfAppointment: e.target.value })} className={inputCls} />
                      </Field>
                    </div>
                  )}
                  {row.letterMode === 'own' && (
                    <div className="mt-2">
                      <FilePicker
                        kind="docsAndImages"
                        onChange={(e) => setRowLetter && setRowLetter(row.key, (e.target.files || [])[0] || null)}
                        files={picked || null}
                      />
                      <p className="text-xs text-slate-500 mt-1">
                        {currentOwn
                          ? `Currently attached: ${currentOwn.originalName}. Choosing a file replaces it.`
                          : hintOf('docsAndImages')}
                      </p>
                      <FieldInvalidInline field={rowFieldKey(row.key, 'letter')} text="Required — upload this company's Appointment Letter" />
                    </div>
                  )}
                  {row.letterMode === 'existing' && (
                    <div className="mt-2">
                      {letterOptions(row.appointmentLetterId, (v) => setRow(row.key, { appointmentLetterId: v }))}
                      <FieldInvalidInline field={rowFieldKey(row.key, 'appointmentLetterId')} text="Required — pick the group letter that covers this company" />
                    </div>
                  )}
                </Field>
                <FieldCommentInline field={rowFieldKey(row.key)} />
              </div>
            );
          })}
          <button type="button" onClick={addRow} className={`${btnGhost} inline-flex items-center gap-1.5 border border-slate-200`}>
            <Plus className="w-4 h-4" /> Add Company
          </button>
        </div>
      </FormSection>

      <FormSection step={3} title="Appointment Letter">
        {!anyShared ? (
          <p className="text-sm text-slate-500">Every company above brings its own letter and date — nothing to attach here.</p>
        ) : (
          <>
            <Field label="Date of Appointment" required field="sharedDateOfAppointment" hint="One date for every company using this letter — recorded on each company in the client master; engagements read it from there.">
              <input type="date" value={f.sharedDateOfAppointment} onChange={(e) => set('sharedDateOfAppointment', e.target.value)} className={inputCls} />
            </Field>
            {groupLetters.length > 0 && (
              <div className="flex flex-wrap gap-4">
                {[['upload', 'Upload a new letter'], ['existing', 'Covered by an existing group letter']].map(([v, label]) => (
                  <label key={v} className="flex items-center gap-2 text-sm text-slate-700">
                    <input type="radio" name="appointmentMode" value={v} checked={f.appointmentMode === v}
                      onChange={() => set('appointmentMode', v)} />
                    {label}
                  </label>
                ))}
              </div>
            )}
            {f.appointmentMode === 'existing' && groupLetters.length > 0 ? (
              <Field label="Group Letter" field="appointmentLetterId" hint="The group's current appointment letters — pick the one that covers these companies.">
                {letterOptions(f.appointmentLetterId, (v) => set('appointmentLetterId', v))}
              </Field>
            ) : (
              <Field
                label="Appointment Letter"
                field="appointmentLetter"
                hint={currentLetter
                  ? `Currently attached: ${currentLetter.originalName}.`
                  : hintOf('docsAndImages')}
              >
                <FilePicker
                  kind="docsAndImages"
                  onChange={(e) => setAppointmentFile((e.target.files || [])[0] || null)}
                  files={appointmentFile}
                />
              </Field>
            )}
          </>
        )}
      </FormSection>
    </>
  );
};

/** Per-row letter uploads, keyed by row key. */
export const useRowLetters = () => {
  const [rowLetters, setRowLetters] = useState({});
  const setRowLetter = (key, file) => setRowLetters((prev) => {
    const next = { ...prev };
    if (file) next[key] = file; else delete next[key];
    return next;
  });
  return [rowLetters, setRowLetter];
};

const WorkflowClientNew = () => {
  const navigate = useNavigate();

  const [groups, setGroups] = useState([]);
  const [f, setF] = useState(emptyClientForm());
  const [structureFile, setStructureFile] = useState(null);
  const [appointmentFile, setAppointmentFile] = useState(null);
  const [rowLetters, setRowLetter] = useRowLetters();
  const [files, setFiles] = useState([]);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF((prev) => ({ ...prev, [k]: v }));

  useEffect(() => {
    (async () => {
      try {
        const data = await fetchJSON('/api/workflow/client/options');
        setGroups(data.groups || []);
      } catch (e) { setError(e.message); }
    })();
  }, []);

  // Everything still empty, keyed to the control it belongs to — after the
  // first Submit the page marks those controls and lists them in the banner.
  const gaps = useMemo(() => clientRequiredGaps(f, { rowLetters }), [f, rowLetters]);
  const validation = useFormValidation(gaps);
  const checks = useCompanyChecks(f.entities);
  const blocked = hasCompanyBlockers(checks);

  const submit = async () => {
    if (!validation.validate()) { setError(null); return; }
    if (blocked) { setError('A company on this request is already registered or under review — see the note under that company.'); return; }
    setSaving(true);
    setError(null);
    try {
      const data = await fetchJSON('/api/workflow/client', {
        method: 'POST',
        body: clientFormData(f, { structureFile, appointmentFile, rowLetters, files }),
      });
      navigate(`/workflow/client/${data.request.id}`);
    } catch (e) { setError(e.message); setSaving(false); }
  };

  return (
    <FormShell
      backTo="/workflow/client"
      backLabel="New Client"
      title="New Client Request"
      subtitle="Register one or more companies of the same group."
      error={error || validation.error}
      onSubmit={submit}
      submitting={saving}
      submitDisabled={blocked}
    >
      <FormValidationProvider value={validation.providerValue}>
        <ClientFormFields
          f={f}
          set={set}
          groups={groups}
          structureFile={structureFile}
          setStructureFile={setStructureFile}
          appointmentFile={appointmentFile}
          setAppointmentFile={setAppointmentFile}
          rowLetters={rowLetters}
          setRowLetter={setRowLetter}
          checks={checks}
        />

        <FormSection step={4} title="Attachments">
          <Field label="Supporting Files" hint={`e.g. BizFile or other evidence for the reviewer. ${hintOf('docsAndImages', { multiple: true })}`}>
            <FilePicker
              multiple
              kind="docsAndImages"
              onChange={(e) => setFiles(Array.from(e.target.files || []))}
              files={files}
            />
          </Field>
        </FormSection>

        <FormSection step={5} title="Remarks">
          <Field label="Remarks">
            <textarea rows="2" value={f.remarks} onChange={(e) => set('remarks', e.target.value)} className={inputCls} />
          </Field>
        </FormSection>

        <FormSection step={6} title="Reviewer">
          <Field label="Reviewer" required field="reviewerName" hint={MASTER_REVIEWER_HINT}>
            <ReviewerSelect roster="master" value={f.reviewerStaffId} onChange={(v) => set('reviewerStaffId', v)} />
          </Field>
        </FormSection>
      </FormValidationProvider>
    </FormShell>
  );
};

export default WorkflowClientNew;
